import { computed } from '@preact/signals';
import { useShallow } from 'zustand/shallow';
import { state } from '../global-state/audio-signals';
import { idToEpisode, usePodcastsStore } from '../global-state/podcasts';
import { removeFromRecentEpisodes, useUserDataStore } from '../global-state/user-data';
import { Episode, Podcast, PodcastId } from '../models/podcasts';
import getThumbUrl from '../utils/get-thumb';
import { isOnServer } from '../utils/helpers';
import { trackContinueListeningClick } from '../utils/plausible-events';
import { truthy } from '../utils/typescript';
import { Icon } from './icon';
import podcastStyle from './latest-podcasts/style.module.scss';
import PlayButton from './play-button';

const isPlayingSignal = computed(() => state.value === 'playing');

export function ContinueListeningToEpisodes() {
  const podcasts = usePodcastsStore((s) => s.podcasts);
  const continueListeningEpisodeIds = useUserDataStore(
    useShallow((s) =>
      s.recentEpisodeIds
        .map((id) => {
          if (
            s.episodesProgress[id] &&
            s.episodesProgress[id][0] > 0 && // User has actually started episode (also set to zero after finished with it)
            s.episodesProgress[id][1] > 5 * 60 // User has at least 5 minutes left
          )
            return id;
        })
        .filter(truthy)
        .slice(0, 10)
    )
  );
  const continueListeningEpisodes = continueListeningEpisodeIds
    .map((id) => {
      const podcast = podcasts[id.split('|')[0] as PodcastId];
      const episode = idToEpisode(id);
      if (podcast && episode) {
        return [id, podcast, episode];
      }
    })
    .filter(truthy);

  const showSkeleton =
    isOnServer ||
    Boolean(continueListeningEpisodeIds.length && !continueListeningEpisodes.length);

  const itemWrapperStyles = {
    width: '180px',
    height: '4.5rem',
    paddingRight: '10px',
    position: 'relative',
    lineHeight: '18px',
    fontSize: '14px',
    paddingTop: '2px',
  };

  return continueListeningEpisodeIds.length ? (
    <>
      <h2 class="my-1">Continue Listening</h2>
      <div class={podcastStyle.podcastRow}>
        {showSkeleton && (
          <>
            <div style={itemWrapperStyles}>
              <div
                class="float-left h-8 w-8"
                style={{
                  margin: '2px 4px 4px 0',
                  borderRadius: '3px',
                  backgroundColor: '#e7e7e7',
                }}
              ></div>
              <div className="absolute bottom-0 w-full bg-white">
                <button class="button button--play w-24" style="border-color: #ccc" />
              </div>
            </div>
          </>
        )}
        {continueListeningEpisodes.map((result) => {
          const [compoundId, podcast, episode] = result as [string, Podcast, Episode];
          const clickPlayButtonWhenClickedAnywhere = (event: any) => {
            if (event.currentTarget.parentElement)
              event.currentTarget.parentElement.scrollLeft = 0;
            if (event.target.nodeName !== 'BUTTON') {
              if (!isPlayingSignal.value) trackContinueListeningClick(episode); // Ignore if pausing
              event.currentTarget.querySelector('.button--play').click();
            }
          };
          return (
            <div
              key={compoundId}
              onClick={clickPlayButtonWhenClickedAnywhere}
              style={itemWrapperStyles}
            >
              <img
                src={getThumbUrl(podcast, 32)}
                alt={podcast.name}
                class="float-left w-8"
                style={{
                  margin: '2px 4px 4px 0',
                  borderRadius: '3px',
                }}
              />
              <button
                title="Remove from recent episodes"
                onClick={() => removeFromRecentEpisodes(compoundId)}
                class="float-right -mt-[2px] flex h-5 w-5 items-center justify-center rounded-full border hover:bg-gray-100 focus:bg-gray-200 focus:outline-blue-500"
              >
                <Icon class="h-3 w-3" sprite="close" />
              </button>
              <span class="text-[13px]">{episode.title}</span>
              <div className="absolute bottom-0 w-full bg-white">
                <PlayButton
                  episode={episode}
                  onPlay={() => trackContinueListeningClick(episode)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  ) : null;
}
